import { useId, useState } from "react";
import { CartesianGrid, Customized, Line, LineChart, XAxis, YAxis, type TooltipProps } from "recharts";
import { BarChart3, Table2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatMesAno } from "@/lib/format";
import { cn } from "@/lib/utils";

// Categóricas 1–3 da paleta de referência da skill de dataviz, validadas juntas
// (todos os pares, superfície branca): CVD ΔE ≥ 9,2, visão normal ≥ 24. A 3ª
// (água) fica abaixo de 3:1 de contraste: por isso a alternância para tabela.
const CORES_SERIE = ["#2a78d6", "#eb6834", "#1baf7a"] as const;

const corDa = (s: { cor?: string }, i: number) => s.cor ?? CORES_SERIE[i];

export interface Serie {
  key: string;
  label: string;
  valores: number[];
  /** Cor fixa (semântica); sem ela, a categórica da posição. */
  cor?: string;
}

interface GraficoLinhasProps {
  titulo: string;
  /** Meses "yyyy-MM", um por ponto. */
  meses: string[];
  series: Serie[];
  formatValor: (v: number) => string;
  /** "tv": sem card nem alternância para tabela, ocupa a altura do pai, tema escuro e fontes grandes. */
  variante?: "padrao" | "tv";
}

function Tooltip({ active, payload, label, series, formatValor }: TooltipProps<number, string> & { series: Serie[]; formatValor: (v: number) => string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="grid min-w-[10rem] gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <p className="font-medium text-foreground">{label}</p>
      {series.map((s, i) => {
        const item = payload.find((p) => p.dataKey === s.key);
        return (
          <div key={s.key} className="flex items-center gap-2">
            <span className="h-2.5 w-1 shrink-0 rounded-[2px]" style={{ background: corDa(s, i) }} />
            <span className="flex-1 text-muted-foreground">{s.label}</span>
            <span className="font-mono font-medium tabular-nums text-foreground">{formatValor(Number(item?.value ?? 0))}</span>
          </div>
        );
      })}
    </div>
  );
}

export function GraficoLinhas({ titulo, meses, series, formatValor, variante = "padrao" }: GraficoLinhasProps) {
  const [tabela, setTabela] = useState(false);
  const tituloId = useId();

  const config: ChartConfig = Object.fromEntries(series.map((s, i) => [s.key, { label: s.label, color: corDa(s, i) }]));
  const dados = meses.map((mes, i) => ({
    mes: formatMesAno(mes),
    ...Object.fromEntries(series.map((s) => [s.key, s.valores[i] ?? 0])),
  }));

  if (variante === "tv") return <GraficoTv titulo={titulo} dados={dados} series={series} config={config} formatValor={formatValor} />;

  return (
    <section aria-labelledby={tituloId} className="rounded-xl border border-border bg-card p-5 shadow-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 id={tituloId} className="text-base font-semibold text-foreground">
          {titulo}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setTabela((t) => !t)}
          aria-label={tabela ? `Ver ${titulo} como gráfico` : `Ver ${titulo} como tabela`}
        >
          {tabela ? <BarChart3 className="h-4 w-4" /> : <Table2 className="h-4 w-4" />}
          <span className="ml-1.5">{tabela ? "Gráfico" : "Tabela"}</span>
        </Button>
      </div>

      {tabela ? (
        <div className="max-h-72 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mês</TableHead>
                {series.map((s) => (
                  <TableHead key={s.key} className="text-right">
                    {s.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {dados.map((linha) => (
                <TableRow key={linha.mes}>
                  <TableCell>{linha.mes}</TableCell>
                  {series.map((s) => (
                    <TableCell key={s.key} className="text-right tabular-nums">
                      {formatValor(Number((linha as Record<string, unknown>)[s.key]))}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <ChartContainer config={config} className="aspect-auto h-72 w-full">
          <LineChart data={dados} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="mes" tickLine={false} axisLine={false} tickMargin={8} minTickGap={16} />
            <YAxis tickLine={false} axisLine={false} width={48} tickFormatter={(v: number) => formatValor(v)} allowDecimals={false} />
            <ChartTooltip cursor={{ strokeDasharray: "3 3" }} content={<Tooltip series={series} formatValor={formatValor} />} />
            <ChartLegend verticalAlign="top" content={<ChartLegendContent />} />
            {series.map((s, i) => (
              <Line
                key={s.key}
                dataKey={s.key}
                type="monotone"
                stroke={corDa(s, i)}
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2, stroke: "hsl(var(--card))", fill: corDa(s, i) }}
                activeDot={{ r: 5, strokeWidth: 2, stroke: "hsl(var(--card))" }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ChartContainer>
      )}
    </section>
  );
}

interface GraficoTvProps {
  titulo: string;
  dados: Record<string, unknown>[];
  series: Serie[];
  config: ChartConfig;
  formatValor: (v: number) => string;
}

/** Na TV não há hover: sem tooltip, legenda própria em fonte grande, linhas mais grossas. */
function GraficoTv({ titulo, dados, series, config, formatValor }: GraficoTvProps) {
  return (
    <figure aria-label={titulo} className="flex h-full flex-col gap-6">
      <figcaption className="flex flex-wrap gap-x-10 gap-y-2 text-2xl text-slate-300">
        {series.map((s, i) => (
          <span key={s.key} className="flex items-center gap-3">
            <span className="h-1.5 w-8 rounded-full" style={{ background: corDa(s, i) }} />
            {s.label}
          </span>
        ))}
      </figcaption>
      <ChartContainer
        config={config}
        className={cn("aspect-auto min-h-0 w-full flex-1 text-xl", "[&_.recharts-cartesian-axis-tick_text]:!fill-slate-400")}
      >
        {/* right: espaço para os rótulos do último mês. */}
        <LineChart data={dados} margin={{ top: 16, right: 128, left: 8, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.1)" />
          <XAxis dataKey="mes" tickLine={false} axisLine={false} tickMargin={16} minTickGap={32} height={56} />
          <YAxis tickLine={false} axisLine={false} width={96} tickFormatter={(v: number) => formatValor(v)} allowDecimals={false} />
          {series.map((s, i) => (
            <Line
              key={s.key}
              dataKey={s.key}
              type="monotone"
              stroke={corDa(s, i)}
              strokeWidth={4}
              dot={{ r: 6, strokeWidth: 3, stroke: "#020617", fill: corDa(s, i) }}
              isAnimationActive={false}
            />
          ))}
          <Customized component={(estado: EstadoGrafico) => <RotulosFinais {...estado} series={series} formatValor={formatValor} />} />
        </LineChart>
      </ChartContainer>
    </figure>
  );
}

/** Subconjunto do estado que o recharts passa ao <Customized>. */
interface EstadoGrafico {
  formattedGraphicalItems?: { item: { props: { dataKey?: unknown } }; props: { points?: { x: number; y: number; value?: unknown }[] } }[];
  offset?: { top: number; height: number };
}

const ALTURA_ROTULO = 30;

/**
 * Valor do último mês no fim de cada linha, na cor da série — um número por linha em vez
 * de um por ponto. Rótulos próximos (ex. séries zeradas) são afastados na vertical.
 */
function RotulosFinais({
  formattedGraphicalItems = [],
  offset,
  series,
  formatValor,
}: EstadoGrafico & { series: Serie[]; formatValor: (v: number) => string }) {
  const rotulos = series
    .flatMap((s, i) => {
      const ultimo = formattedGraphicalItems.find((g) => g.item.props.dataKey === s.key)?.props.points?.at(-1);
      return ultimo ? [{ key: s.key, cor: corDa(s, i), x: ultimo.x, y: ultimo.y, texto: formatValor(Number(ultimo.value ?? 0)) }] : [];
    })
    .sort((a, b) => a.y - b.y);

  for (let i = 1; i < rotulos.length; i++) {
    rotulos[i]!.y = Math.max(rotulos[i]!.y, rotulos[i - 1]!.y + ALTURA_ROTULO);
  }
  // Se o empurrão passou da base do gráfico, sobe só os rótulos encostados — subir o bloco
  // todo cortava o do topo em telas baixas.
  const base = offset ? offset.top + offset.height : Infinity;
  const ultimo = rotulos.at(-1);
  if (ultimo && ultimo.y > base) ultimo.y = base;
  for (let i = rotulos.length - 2; i >= 0; i--) {
    rotulos[i]!.y = Math.min(rotulos[i]!.y, rotulos[i + 1]!.y - ALTURA_ROTULO);
  }

  return (
    <g>
      {rotulos.map((r) => (
        <text key={r.key} x={r.x + 16} y={r.y} dy="0.35em" fill={r.cor} fontSize={26} fontWeight={600} className="tabular-nums">
          {r.texto}
        </text>
      ))}
    </g>
  );
}
